import * as Yup from 'yup';
import { useState } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFTextField, RHFCheckbox } from '../../../components/hook-form';
// utils
import { AUTH_NOTICE_TEXT, AuthNotice, clearAuthNotice, peekAuthNotice } from '../../../utils/authNotice';
// @types
import { StaffOtpChallenge } from '../../../@types/auth';
//
import StaffCodeStep from './StaffCodeStep';

// ----------------------------------------------------------------------
// Phase 4b.4: two-step login. If the BE returns mfaRequired the form
// re-renders with a single OTP input. Resubmitting with the code (plus
// the still-cached email + password from local state) completes sign-in.
//
// Staff sign-in code (auth/staff-otp): for a staff account sign-in then
// answers otpRequired and the form moves to a third step, the code we sent
// by email / WhatsApp (StaffCodeStep). The email + password are kept in this
// component's state only, so that step can ask for a new code.
// ----------------------------------------------------------------------

type Credentials = { email: string; password: string };

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
};

type MfaFormValues = {
  mfaCode: string;
  afterSubmit?: string;
};

export default function LoginForm() {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();
  const [showPassword, setShowPassword] = useState(false);
  const [mfaStage, setMfaStage] = useState<null | Credentials>(null);
  const [codeStage, setCodeStage] = useState<
    null | (Credentials & { challenge: StaffOtpChallenge })
  >(null);
  // A note left by the app when it signed the person out (session over,
  // password changed). It stays until the person signs in again, so a
  // remount of this form (guard → login route) does not lose it.
  const [notice, setNotice] = useState<AuthNotice | null>(() => peekAuthNotice());

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email('Email must be a valid email address')
      .required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = { email: '', password: '', remember: true };

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    setValue,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = methods;
  const values = watch();

  const onSubmit = async (data: FormValuesProps) => {
    setNotice(null);
    clearAuthNotice();
    try {
      const credentials = { email: data.email.toLowerCase(), password: data.password };
      const result = await login(credentials.email, credentials.password);
      if (result.mfaRequired) {
        setMfaStage(credentials);
      } else if (result.otpRequired) {
        setCodeStage({ ...credentials, challenge: result });
      }
    } catch (error: any) {
      const { remember } = values;
      reset();
      setValue('remember', remember);
      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  // Back from any step returns to the email/password form (email kept,
  // password cleared).
  const backToCredentials = () => {
    const email = codeStage?.email || mfaStage?.email || '';
    setMfaStage(null);
    setCodeStage(null);
    reset({ ...defaultValues, email });
  };

  if (codeStage) {
    return (
      <StaffCodeStep
        email={codeStage.email}
        password={codeStage.password}
        challenge={codeStage.challenge}
        onMfaRequired={() => {
          setMfaStage({ email: codeStage.email, password: codeStage.password });
          setCodeStage(null);
        }}
        onBack={backToCredentials}
      />
    );
  }

  if (mfaStage) {
    return (
      <MfaStep
        email={mfaStage.email}
        password={mfaStage.password}
        onOtpRequired={(challenge) => {
          setCodeStage({ ...mfaStage, challenge });
          setMfaStage(null);
        }}
        onBack={backToCredentials}
      />
    );
  }

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {notice && !errors.afterSubmit && (
          <Alert severity="info" data-testid="auth-notice">
            {AUTH_NOTICE_TEXT[notice]}
          </Alert>
        )}
        {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        <RHFTextField name="email" label="Email address" />
        <RHFTextField
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                  <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ my: 2 }}
      >
        <RHFCheckbox name="remember" label="Remember me" />
      </Stack>
      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}

// ----------------------------------------------------------------------

function MfaStep({
  email,
  password,
  onOtpRequired,
  onBack,
}: {
  email: string;
  password: string;
  onOtpRequired: (challenge: StaffOtpChallenge) => void;
  onBack: () => void;
}) {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();

  const schema = Yup.object().shape({
    mfaCode: Yup.string()
      .matches(/^\d{6}$/, 'Enter the 6-digit code from your authenticator app')
      .required('MFA code is required'),
  });

  const methods = useForm<MfaFormValues>({
    resolver: yupResolver(schema),
    defaultValues: { mfaCode: '' },
  });

  const {
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: MfaFormValues) => {
    try {
      const result = await login(email, password, data.mfaCode);
      if (result.otpRequired) {
        // Staff account: the authenticator code was right; now the emailed code.
        onOtpRequired(result);
        return;
      }
      if (result.mfaRequired && isMountedRef.current) {
        // Should not happen — BE only returns mfaRequired on the first call.
        setError('afterSubmit', {
          message: 'Unexpected MFA challenge. Try logging in again.',
        });
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        setError('afterSubmit', { ...err, message: err?.message || 'Invalid code' });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle1">Two-factor authentication</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Enter the 6-digit code from your authenticator app.
          </Typography>
        </Box>

        {errors.afterSubmit && (
          <Alert severity="error">{errors.afterSubmit.message}</Alert>
        )}

        <RHFTextField
          name="mfaCode"
          label="Authentication code"
          inputProps={{
            inputMode: 'numeric',
            pattern: '[0-9]*',
            maxLength: 6,
            autoComplete: 'one-time-code',
          }}
        />

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
          >
            Verify
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
